import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AWSResources, DynamoDBMetrics, TimestreamMetrics } from '../../types/MetricWidgetTypes';
import { Autocomplete, Checkbox, Chip, Grid, TextField } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getResourceDimension } from '../../data/aws/cloudwatch/resourceDimensions';
const icon = _jsx(CheckBoxOutlineBlankIcon, { fontSize: "small" });
const checkedIcon = _jsx(CheckBoxIcon, { fontSize: "small" });
const DimensionSelector = ({ resource, resourceMetric, setSelectedDimensionMapList }) => {
    const dimensionMapList = getResourceDimension(resource);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const additionalDimensionOption = useRef([]);
    useEffect(() => {
        if (resource === AWSResources.DynamoDB) {
            additionalDimensionOption.current = dimensionMapList.filter((dimensionMap) => dimensionMap.dimensionName === 'TableName');
        }
        setSelectedDimensionMapList([...additionalDimensionOption.current, ...selectedOptions]);
    }, [selectedOptions]);
    if ([AWSResources.Lambda, AWSResources.SQS].includes(resource)) {
        return (_jsx(Grid, { container: true, spacing: 2, size: 12, children: _jsx(SingleDimensionCheckbox, { dimensionMap: dimensionMapList[0], selectedOptions, setSelectedOptions }) }));
    }
    else if (resource === AWSResources.DynamoDB) {
        const dynamoDBTableIndexList = useMemo(() => {
            if (resourceMetric === DynamoDBMetrics.ConsumedReadCapacityUnits || resourceMetric === DynamoDBMetrics.ConsumedWriteCapacityUnits) {
                const tableIndex = selectedOptions.filter((dimension) => dimension.dimensionName === 'GlobalSecondaryIndexName');
                return tableIndex;
            }
            else
                return [];
        }, [resourceMetric]);
        useEffect(() => {
            setSelectedOptions(dynamoDBTableIndexList);
        }, [dynamoDBTableIndexList]);
        return (_jsx(Grid, { container: true, spacing: 2, size: 12, children: dimensionMapList.map((dimensionMap) => {
                const { dimensionName, dimensionValue } = dimensionMap;
                if (dimensionName === 'TableName') {
                    return _jsx(FixedCheckbox, { fixedOptions: dimensionValue, dimensionName, setSelectedOptions }, dimensionName);
                }
                if (dimensionName === 'GlobalSecondaryIndexName') {
                    if ([DynamoDBMetrics.ConsumedReadCapacityUnits, DynamoDBMetrics.ConsumedWriteCapacityUnits].includes(resourceMetric)) {
                        return _jsx(SingleDimensionCheckbox, { dimensionMap, selectedOptions, setSelectedOptions }, dimensionName);
                    }
                }
                if (dimensionName === 'Operation') {
                    if (resourceMetric === DynamoDBMetrics.SuccessfulRequestLatency) {
                        return _jsx(SingleDimensionCheckbox, { dimensionMap, selectedOptions, setSelectedOptions }, dimensionName);
                    }
                }
            }) }));
    }
    else if (resource === AWSResources.Timestream) {
        const databaseDimensionMap = dimensionMapList.find((dimensionMap) => dimensionMap.dimensionName === 'DatabaseName');
        const databaseOptions = databaseDimensionMap?.dimensionValue;
        const databaseTablePairs = databaseOptions?.map((database) => {
            return [`${database}`, dimensionMapList.find((dimensionMap) => dimensionMap.dimensionName === database)?.dimensionValue];
        });
        const tableOptions = databaseTablePairs ? Object.fromEntries(databaseTablePairs) : undefined;
        const operationOptions = dimensionMapList.find((dimensionMap) => dimensionMap.dimensionName === 'Operation')?.dimensionValue;
        useEffect(() => {
            setSelectedOptions([]);
        }, [resourceMetric]);
        return (_jsxs(Grid, { container: true, spacing: 2, size: 12, children: [resourceMetric === TimestreamMetrics.SuccessfulRequestLatency && databaseOptions && tableOptions && operationOptions && (_jsx(DbTableOpSelector, { resourceMetric, databaseOptions, tableOptions, operationOptions, setSelectedOptions })), resourceMetric !== TimestreamMetrics.SuccessfulRequestLatency && databaseOptions && operationOptions && (_jsx(DbOpSelector, { resourceMetric, databaseOptions, operationOptions, setSelectedOptions }))] }));
    }
};
const SingleDimensionCheckbox = ({ dimensionMap, setSelectedOptions }) => {
    const { dimensionName, dimensionValue } = dimensionMap;
    return (_jsx(Grid, { size: "grow", children: _jsx(Autocomplete, { multiple: true, id: "checkboxes-tags-demo", limitTags: 3, options: dimensionValue, disableCloseOnSelect: true, onChange: (event, newValue) => {
                if (newValue.length > 0) {
                    setSelectedOptions([{ dimensionName, dimensionValue: newValue }]);
                }
                else {
                    setSelectedOptions([]);
                }
            }, renderOption: (props, option, { selected }) => (_jsxs("li", { ...props, children: [_jsx(Checkbox, { icon: icon, checkedIcon: checkedIcon, style: { marginRight: 8 }, checked: selected }), option] })), renderInput: (params) => _jsx(TextField, { ...params, label: dimensionName }) }) }));
};
const FixedCheckbox = ({ fixedOptions, dimensionName, setSelectedOptions }) => {
    const [value, setValue] = React.useState([...fixedOptions]);
    return (_jsx(Grid, { size: "grow", children: _jsx(Autocomplete, { multiple: true, id: "fixed-tags-demo", value: value, onChange: (event, newValue) => {
                setValue([...fixedOptions, ...newValue.filter((option) => fixedOptions.indexOf(option) === -1)]);
            }, options: fixedOptions, renderTags: (tagValue, getTagProps) => 
            // eslint-disable-next-line react/jsx-key
            tagValue.map((option, index) => _jsx(Chip, { label: option, ...getTagProps({ index }), disabled: fixedOptions.indexOf(option) !== -1 })), 
            // style={{ width: 500 }}
            renderInput: (params) => _jsx(TextField, { ...params, label: dimensionName }) }) }));
};
const DbTableOpSelector = ({ resourceMetric, databaseOptions, tableOptions, operationOptions, setSelectedOptions }) => {
    const [selectedDatabase, setSelectedDatabase] = useState(null);
    const [selectedTable, setSelectedTable] = useState([]);
    const [selectedOperation, setSelectedOperation] = useState([]);
    useEffect(() => {
        if (selectedDatabase && selectedTable.length > 0 && selectedOperation.length > 0) {
            setSelectedOptions([
                { dimensionName: 'DatabaseName', dimensionValue: [selectedDatabase] },
                { dimensionName: 'TableName', dimensionValue: selectedTable },
                { dimensionName: 'Operation', dimensionValue: selectedOperation },
            ]);
        }
        else {
            setSelectedOptions([]);
        }
    }, [selectedDatabase, selectedTable, selectedOperation]);
    useEffect(() => {
        if (!selectedDatabase || selectedTable.length === 0) {
            setSelectedOperation([]);
        }
    }, [selectedDatabase, selectedTable]);
    useEffect(() => {
        setSelectedDatabase(null);
        setSelectedTable([]);
        setSelectedOperation([]);
    }, [resourceMetric]);
    return (_jsxs(_Fragment, { children: [_jsx(Grid, { size: "grow", children: _jsx(Autocomplete, { options: databaseOptions, value: selectedDatabase, onChange: (event, newValue) => {
                        setSelectedDatabase(newValue);
                        // Reset the selected table when the database changes
                        setSelectedTable([]);
                    }, renderInput: (params) => _jsx(TextField, { ...params, label: "DatabaseName" }) }) }), _jsx(Grid, { size: "grow", children: _jsx(Autocomplete, { multiple: true, options: selectedDatabase ? tableOptions[selectedDatabase] : [], value: selectedTable, disableCloseOnSelect: true, onChange: (event, newValue) => setSelectedTable(newValue), renderOption: (props, option, { selected }) => (_jsxs("li", { ...props, children: [_jsx(Checkbox, { icon: icon, checkedIcon: checkedIcon, style: { marginRight: 8 }, checked: selected }), option] })), renderInput: (params) => _jsx(TextField, { ...params, label: "TableName" }), disabled: !selectedDatabase }) }), _jsx(Grid, { size: "grow", children: _jsx(Autocomplete, { multiple: true, options: operationOptions ? operationOptions : [], value: selectedOperation, disableCloseOnSelect: true, onChange: (event, newValue) => setSelectedOperation(newValue), renderOption: (props, option, { selected }) => (_jsxs("li", { ...props, children: [_jsx(Checkbox, { icon: icon, checkedIcon: checkedIcon, style: { marginRight: 8 }, checked: selected }), option] })), renderInput: (params) => _jsx(TextField, { ...params, label: "Operation" }), disabled: !selectedDatabase && selectedTable.length === 0 }) })] }));
};
const DbOpSelector = ({ resourceMetric, databaseOptions, operationOptions, setSelectedOptions }) => {
    const [selectedDatabase, setSelectedDatabase] = useState(null);
    const [selectedOperation, setSelectedOperation] = useState([]);
    useEffect(() => {
        if (selectedDatabase && selectedOperation.length > 0) {
            setSelectedOptions([
                { dimensionName: 'DatabaseName', dimensionValue: [selectedDatabase] },
                { dimensionName: 'Operation', dimensionValue: selectedOperation },
            ]);
        }
        else {
            setSelectedOptions([]);
        }
    }, [selectedDatabase, selectedOperation]);
    useEffect(() => {
        setSelectedDatabase(null);
        setSelectedOperation([]);
    }, [resourceMetric]);
    return (_jsxs(_Fragment, { children: [_jsx(Grid, { size: "grow", children: _jsx(Autocomplete, { options: databaseOptions, value: selectedDatabase, onChange: (event, newValue) => {
                        setSelectedDatabase(newValue);
                        setSelectedOperation([]);
                    }, renderInput: (params) => _jsx(TextField, { ...params, label: "DatabaseName" }) }) }), _jsx(Grid, { size: "grow", children: _jsx(Autocomplete, { multiple: true, options: operationOptions ? operationOptions : [], value: selectedOperation, disableCloseOnSelect: true, onChange: (event, newValue) => setSelectedOperation(newValue), renderOption: (props, option, { selected }) => (_jsxs("li", { ...props, children: [_jsx(Checkbox, { icon: icon, checkedIcon: checkedIcon, style: { marginRight: 8 }, checked: selected }), option] })), renderInput: (params) => _jsx(TextField, { ...params, label: "Operation" }), disabled: !selectedDatabase }) })] }));
};
export default DimensionSelector;
